import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from 'classnames';

import { EditorContext } from "components/Editor";
import { createWidget, selectWidget } from "actions/editor";
import { useHotspotParentSelector, useWidgetChildrenSelector } from "components/Editor/hooks/useEditorSession";
import { useContainerSelector } from "components/Editor/hooks/useEditorSession";
import { getStylesForLayout } from "components/Editor/helper/layoutManager";
import { PageWidgetContext } from "../page";
import { CAROUSEL, CONTENT, CAROUSEL_CARD, CONTAINER, FORM, FORM_STEP, PAGE, SECTION, SECTION_COLUMN, SEEK, SLIDE, TEXT } from "constants/widgetTypes";
import { defaultOtpWidgets } from "components/Palette/form";
import StoryboardContext from "components/Storyboard/context";

import { WidgetRegistry } from '../registry';
import { FlexFlowContext, RESET_FLEX_CONTEXT, flowsChildrenInFlex, DEFAULT_FLEX_LAYOUT, allColumnsInFlexMode, CONTENT_DRIVEN_SECTION_HEIGHT, isStretchedWidget, resolveFlexEnabled, breaksOutOfFlexFlow } from '../flex/flexContext';
import { getCardFlexConfig } from '../carousel/card/flexConfig';

import styles from '../../editor.module.scss';
import { INTERACTIVE_CONTENT_TYPES, SEGMENT_STORYBOARDS, SLIDE_STORYBOARDS, STORYBOARD_TYPES } from "constants/storyboardType";
import { breakpoints } from "constants/breakpoints";
import { isIcV2 } from "pages/pages/preview/icV2/helper";

let isFreeSize = false;

function useStableValue(value) {
  const ref = useRef(value);
  if (!_.isEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

function getLayoutResizeKey(layout, widgetType) {
  if (!layout) return '';
  const translate = _.get(layout, 'transform.translate', '');
  if (widgetType === CONTENT) return translate;
  const dim = (v) => (typeof v === 'string' ? v : '');
  return `${dim(layout.height)}|${dim(layout.width)}|${translate}`;
}

const WidgetComponent = ({ widgetProps, ...rest }) => {
  const dispatch = useDispatch();
  const ref = React.useRef(null);

  const id = _.get(widgetProps, 'uid');

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type', '');
  const type = _.get(widgetProps, 'type');
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const { flexMode: inFlexFlow, fitItems, flexLayout } = React.useContext(FlexFlowContext);
  const escapesFlexFlow = breaksOutOfFlexFlow(type);

  const pageWidgetContext = React.useContext(PageWidgetContext);

  const hasHotspotChildren = useHotspotParentSelector(editor, widgetProps);

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const entityType = useSelector(state => _.get(state, `${editor}.present.entityType`));

  const usesDesktopFrameVisibility =
    (type === PAGE &&
      (SEGMENT_STORYBOARDS.includes(storyboardType) ||
        storyboardType === STORYBOARD_TYPES.INTERACTIVE_CONTENT ||
        entityType === "PALETTE_COMPONENT")) ||
    (type === SLIDE && SLIDE_STORYBOARDS.includes(storyboardType));

  const isWidgetHidden =
    (usesDesktopFrameVisibility &&
      _.get(widgetProps, 'properties.desktop.visible', true) === false) ||
    (!usesDesktopFrameVisibility &&
      _.get(widgetProps, `properties.${breakpoint}.visible`, true) === false);

  const container = useContainerSelector(editor, id);

  var widgetChildren = useWidgetChildrenSelector(editor, id) || [];
  widgetChildren = widgetChildren.filter(item => {
    return _.get(item, `properties.${breakpoint}.visible`, true);
  });

  otpForOldForms(widgetProps, widgetChildren, dispatch, editor);

  const flowsOwnChildren = flowsChildrenInFlex(type, widgetProps, breakpoint);
  widgetChildren = _.orderBy(widgetChildren, 'order', 'asc');

  isFreeSize = (type === CAROUSEL || type === SEEK)
    && _.get(widgetProps, 'properties.desktop.free', false);

  const freesizeFlexBreakout = (type === CONTAINER || isFreeSize)
    && breakpoint === 'desktop'
    && INTERACTIVE_CONTENT_TYPES.includes(storyboardType);

  const carouselCurrentSlideFlex = type === CAROUSEL && (() => {
    const currentSlideId = _.get(widgetProps, 'editorState.currentSlide');
    const slide = widgetChildren.find(w => w.uid === currentSlideId)
      || _.find(widgetChildren, { order: 1 });
    return _.get(getCardFlexConfig(slide, breakpoint), 'flexBoxEnabled', false);
  })();

  const carouselEmptyFluidFollowsFlex = useSelector(state => {
    if (type !== CAROUSEL || carouselCurrentSlideFlex) return false;
    const anyFlex = _.some(widgetChildren, (slide) => _.get(getCardFlexConfig(slide, breakpoint), 'flexBoxEnabled', false));
    if (!anyFlex) return false;
    const currentSlideId = _.get(widgetProps, 'editorState.currentSlide');
    const shown = widgetChildren.find(w => w.uid === currentSlideId)
      || _.find(widgetChildren, { order: 1 });
    if (!shown) return false;
    const widgets = _.get(state, `${editor}.present.widgets`);
    return !_.some(widgets, w => w.parent === shown.uid);
  });
  const carouselFlexActive = carouselCurrentSlideFlex || carouselEmptyFluidFollowsFlex;

  const carouselFlexContentDriven = carouselFlexActive;

  const carouselFlexKeepsWidth = carouselFlexActive && isFreeSize && breakpoint === 'desktop';

  const cardInFlexMode = type === CAROUSEL_CARD
    && _.get(getCardFlexConfig(widgetProps, breakpoint), 'flexBoxEnabled', false);

  const fluidCardFollowsFlex = useSelector(state => {
    if (type !== CAROUSEL_CARD || cardInFlexMode) return false;
    const widgets = _.get(state, `${editor}.present.widgets`);
    if (!widgets) return false;
    const parentId = _.get(widgetProps, 'parent');
    return _.some(widgets, w =>
      w.parent === parentId
      && w.uid !== id
      && _.get(getCardFlexConfig(w, breakpoint), 'flexBoxEnabled', false)
    );
  });

  const carouselFluidNeedsHeight = type === CAROUSEL && !carouselFlexActive;

  const sectionAllColumnsFlex = type === SECTION && allColumnsInFlexMode(widgetChildren);

  const pageInFlexMode = type === PAGE && flowsOwnChildren &&
    storyboardType !== STORYBOARD_TYPES.INTERACTIVE_CONTENT;

  const containerInFlexMode = type === CONTAINER && resolveFlexEnabled(widgetProps, breakpoint);

  const containerFluidNeedsHeight = type === CONTAINER && !containerInFlexMode;

  const rawLayout = _.get(widgetProps, `properties.${breakpoint}.layout`);
  const stableLayout = useStableValue(rawLayout);
  const layout = inFlexFlow ? rawLayout : stableLayout;
  const layoutResizeKey = getLayoutResizeKey(layout, type);
  var widgetStyle = React.useMemo(() => {
    try {
      return getStylesForLayout(layout, breakpoint, container, storyboardType, widgetProps.type, isFreeSize);
    } catch (e) {
      console.error(e);
      return {};
    }
  }, [layout, breakpoint, container, isFreeSize]);

  useEffect(() => {
    return () => {
      if (ref.current) {
        editorContext.editorSession.moveableData.removeFrame(ref.current);
      }
      if(editorContext.editorSession.moveableManager.current){
        editorContext.editorSession.moveableManager.current.updateRect();
      }
    }
  }, []);

  useEffect(() => {
    if (ref.current && (!inFlexFlow || escapesFlexFlow)) {
      ['maxWidth', 'flexShrink'].forEach(prop => {
        ref.current.style.removeProperty(prop.replace(/[A-Z]/g, m => '-' + m.toLowerCase()));
      });

      if (sectionAllColumnsFlex) {
        ref.current.style.removeProperty('height');
      }

      if (pageInFlexMode) {
        ref.current.style.removeProperty('height');
      }

      if (containerInFlexMode) {
        ref.current.style.removeProperty('height');
      }

      const frame = editorContext.editorSession.moveableData.createFrame(ref.current, layout);

      if (isIcV2(storyboardType) && type === SECTION) {
        frame.set("width", "100%");
      }

      if (type === SECTION_COLUMN && _.get(widgetProps, 'properties.desktop.flexBoxEnabled', false)) {
        frame.remove("height");
      }

      if (containerInFlexMode) {
        frame.remove("height");
      }

      if (sectionAllColumnsFlex) {
        frame.remove("height");
      }

      if (pageInFlexMode) {
        frame.remove("height");
      }

      if (cardInFlexMode) {
        frame.remove("height");
      }

      if (fluidCardFollowsFlex) {
        frame.remove("height");
        ref.current.style.removeProperty('height');
      }

      if (isStretchedWidget(widgetProps)) {
        const translate = frame.get('transform', 'translate');
        const y = _.split(translate, ',')[1]?.trim() || '0px';
        frame.set('transform', 'translate', `0px, ${y}`);
      }
      
      if (carouselFlexContentDriven) {
        if (!carouselFlexKeepsWidth) {
          frame.remove("width");
        }
        frame.remove("height");
        ref.current.style.removeProperty('height');
      }

      if (type === CAROUSEL && breakpoint === 'mobile') {
        frame.set("width", `${breakpoints.mobile.width}px`);
      }

      editorContext.editorSession.moveableData.render(ref.current, frame);

      if (editorContext.editorSession.moveableManager.current) {
        editorContext.editorSession.moveableManager.current.updateRect();
      }
    } else if (ref.current && inFlexFlow && !escapesFlexFlow) {
      const { preserveSize, preserveWidth } = WidgetRegistry.flexFlow(widgetProps);
      const keepWidth = preserveSize || preserveWidth;
      const keepHeight = preserveSize || carouselFluidNeedsHeight || containerFluidNeedsHeight;
      const staleProps = ['top', 'left', 'transform'];
      if (!keepHeight) staleProps.push('height');
      if (!keepWidth) staleProps.push('width');
      staleProps.forEach(prop => {
        ref.current.style.removeProperty(prop);
      });

      if (containerInFlexMode) {
        ref.current.style.removeProperty('height');
      }

      if (widgetStyle.width != null) ref.current.style.width = widgetStyle.width;
      if (widgetStyle.height != null && !containerInFlexMode) ref.current.style.height = widgetStyle.height;
    }

    if (hasHotspotChildren) {
      ref.current && ref.current.setAttribute("data-hotspot", true);
    } else {
      ref.current && ref.current.removeAttribute("data-hotspot");
    }

    if (inFlexFlow && freesizeFlexBreakout) {
      ref.current && ref.current.setAttribute("data-flex-freesize", "true");
    } else {
      ref.current && ref.current.removeAttribute("data-flex-freesize");
    }
  }, [breakpoint, layout, widgetStyle, inFlexFlow, escapesFlexFlow, freesizeFlexBreakout, carouselCurrentSlideFlex, carouselFlexActive, carouselFlexContentDriven, carouselFlexKeepsWidth, carouselFluidNeedsHeight, containerFluidNeedsHeight, sectionAllColumnsFlex, pageInFlexMode, containerInFlexMode, cardInFlexMode, fluidCardFollowsFlex]);

  // Segment storyboard overflow is handled once per page in PageWidgetComponent
  // to avoid subscribing every Widget to global selection (full-tree re-renders).
  useEffect(() => {
    if (SEGMENT_STORYBOARDS.includes(storyboardType)) {
      return;
    }
    pageWidgetContext.onResize();
  }, [breakpoint, pageWidgetContext.onResize, storyboardType, layoutResizeKey]);

  if (isWidgetHidden) {
    return null;
  }

  const Component = getWidgetComponentFotType(type);
  if(!Component){
    console.warn(`Component not found for type ${type}`);
    return false;
  }

  var widgetStyle = getStylesForLayout(layout, breakpoint, container, storyboardType, widgetProps.type, isFreeSize);

  if (inFlexFlow && !escapesFlexFlow) {
    const flexItemWidth = toCssDimension(widgetStyle.width);
    const flexItemHeight = toCssDimension(widgetStyle.height);

    const { preserveSize, preserveWidth } = WidgetRegistry.flexFlow(widgetProps);
    const keepHeight = preserveSize || carouselFluidNeedsHeight || containerFluidNeedsHeight;
    const honorsExplicitWidth = isFreeSize || preserveWidth;
    const dropProps = ['position', 'left', 'top', 'transform', 'marginLeft'];
    if (!keepHeight) {
      dropProps.push('height');
    }
    if (!preserveSize && !preserveWidth) {
      dropProps.push('width');
    }
    widgetStyle = _.omit(widgetStyle, dropProps);
    widgetStyle.position = 'relative';
    widgetStyle.zIndex = 1;

    if (fitItems) {
      if (flexItemWidth) {
        widgetStyle.width = flexItemWidth;
        if (flexLayout?.flexWrap !== 'nowrap' && !honorsExplicitWidth) {
          widgetStyle.maxWidth = '100%';
        }
        widgetStyle.flexShrink = 0;
      }
      if (flexItemHeight && !preserveWidth) {
        widgetStyle.height = flexItemHeight;
      }
    }

    if (!fitItems && preserveWidth && flexItemWidth && flexLayout?.flexWrap !== 'nowrap' && !honorsExplicitWidth) {
      widgetStyle.maxWidth = '100%';
    }

    const isColumnAxis = flexLayout?.flexDirection === 'column'
      || flexLayout?.flexDirection === 'columnReverse';
    if (!fitItems && preserveSize && flexItemWidth && isColumnAxis) {
      widgetStyle.maxWidth = '100%';
    }

    if (flexLayout?.flexWrap === 'nowrap') {
      widgetStyle.flexShrink = 0;
      if (!fitItems && !preserveSize && !preserveWidth && flexItemWidth) {
        widgetStyle.width = flexItemWidth;
      }
    }

    if (freesizeFlexBreakout && flexItemWidth) {
      const breakoutMargin = `min(0px, calc((100% - ${flexItemWidth}) / 2))`;
      widgetStyle.flexShrink = 0;
      widgetStyle.marginLeft = breakoutMargin;
      widgetStyle.marginRight = breakoutMargin;
    }

    if (containerInFlexMode) {
      delete widgetStyle.height;
    }

    if (carouselFlexContentDriven) {
      delete widgetStyle.height;
    }

    if (sectionAllColumnsFlex) {
      widgetStyle.height = CONTENT_DRIVEN_SECTION_HEIGHT;
    }

    if (type === SECTION && !sectionAllColumnsFlex && flexItemHeight) {
      widgetStyle.height = flexItemHeight;
    }
  }

  var children = widgetChildren?.map(item => {
    return <Widget key={item.uid} widgetProps={item} editable />
  });

  var element = React.createElement(Component, {
    key: id,
    widgetRef: ref,
    widgetProps: widgetProps,
    className: cx(styles.widget, 'widget'),
    style: widgetStyle,
    inFlexFlow,
    parentFlexLayout: inFlexFlow ? flexLayout : null,
    ...rest
  }, children);

  if (inFlexFlow && !flowsOwnChildren) {
    element = (
      <FlexFlowContext.Provider value={RESET_FLEX_CONTEXT}>
        {element}
      </FlexFlowContext.Provider>
    );
  }

  return element;
}

export const Widget = React.memo(WidgetComponent);

function toCssDimension(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`;
  if (typeof value === 'string' && value.trim() !== '') return value;
  return undefined;
}

function getWidgetComponentFotType(type) {
  var config = WidgetRegistry.widgets[type];
  if (!config) {
    console.error(`Widget config for type ${type} not found. Check if the widget type is registered`);
    return false;
  }

  return config['editor'];
}

function otpForOldForms(widgetProps, widgetChildren, dispatch, editor) {
  if (_.get(widgetProps, 'type') === FORM) {
    if (widgetChildren?.length === 2) {
      const otpState = {
        type: FORM_STEP,
        name: "OTP",
        properties: {
          "desktop": {
            "visible": true,
            "layout": {
              "transform": {
                "translate": "0px, 0px",
                "rotate": "0deg"
              },
              "width": null,
              "height": null
            }
          },
          "mobile": {
            "visible": true,
            "layout": {
              "transform": {
                "translate": "0px, 0px",
                "rotate": "0deg"
              },
              "width": null,
              "height": null
            }
          }
        }
      };
      dispatch(createWidget(editor, [otpState], widgetProps.uid));
      dispatch(selectWidget(editor, []));
    }
  }
  if (_.get(widgetProps, 'type') === FORM_STEP && _.get(widgetProps, 'name') === 'OTP') {
    if (widgetChildren?.length === 0) {
      const otpWidgets = defaultOtpWidgets;
      dispatch(createWidget(editor, otpWidgets, widgetProps.uid));
      dispatch(selectWidget(editor, []));
    }
  }
}